export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown
  ) {
    super(`API ${status}`);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, json);
  return json as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body),
};

export interface Session {
  user: { id: string; email: string; name: string; realm: 'ops' | 'client' };
  memberships: Array<{ tenantId: string; role: string }>;
}

export interface Tenant {
  id: string;
  slug: string;
  displayName: string;
  status: string;
  timezone: string;
  packOverrides: Record<string, unknown>;
  businessProfile?: Record<string, unknown>;
}

export interface WorkItem {
  id: string;
  tenantId: string;
  family: string;
  type: string;
  channel: string | null;
  state: string;
  payload: Record<string, unknown>;
  scheduledFor: string | null;
  quotaPeriod: string | null;
  generatedBy: string | null;
  externalRef: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  updatedAt: string;
}

export interface WorkItemEvent {
  id: string;
  actor: string;
  fromState: string | null;
  toState: string;
  note: string | null;
  diff: { changed: string[] } | null;
  createdAt: string;
}

export interface QuotaGauge {
  featureKey: string;
  period: string;
  used: number;
  quota: number;
  warn: boolean;
}

export interface PublishMode {
  tenantId: string;
  channel: string;
  mode: 'manual' | 'client_review' | 'auto';
  dailyCap: number | null;
}

export interface Plan {
  id: string;
  key: string;
  name: string;
  level: number;
  monthlyPriceCents: number;
  setupPriceCents: number;
  hubProductSlug: string | null;
}

export const REJECTION_REASONS = ['off_brand', 'factual', 'quality', 'timing', 'duplicate', 'other'] as const;

/** Ops-available next transitions per state (server state machine is authoritative). */
export const OPS_NEXT: Record<string, Array<{ to: string; label: string; reason?: boolean }>> = {
  queued: [{ to: 'generated', label: 'Mark authored' }],
  generated: [{ to: 'in_review', label: 'Send to review' }],
  in_review: [
    { to: 'approved', label: 'Approve' },
    { to: 'client_review', label: 'Send to client' },
    { to: 'rejected', label: 'Reject', reason: true },
  ],
  held: [{ to: 'in_review', label: 'Move to review' }],
  failed: [
    { to: 'approved', label: 'Retry publish' },
    { to: 'queued', label: 'Regenerate' },
  ],
};
